import type { ChatOrderProduct } from './ChatOrderProductCandidatesModal';
import type { ProductDeliveryState } from './hooks/useChatOrderSelection';
import QuantityStepper from './QuantityStepper';
import DeliveryFeeSection from './DeliveryFeeSection';

interface ProductCardProps {
  product: ChatOrderProduct;
  formatPrice: (amount: number) => string;
  onToggleItem: (params: { productId: string; optionId: string | null; price: number }) => void;
  onIncreaseQuantity: (productId: string, optionId: string | null) => void;
  onDecreaseQuantity: (productId: string, optionId: string | null) => void;
  onToggleDeliveryFee: (productId: string) => void;
  onSelectDeliveryFeeOption: (productId: string, index: number) => void;
  isItemSelected: (productId: string, optionId: string | null) => boolean;
  getItemQuantity: (productId: string, optionId: string | null) => number;
  deliveryState: ProductDeliveryState;
  isProductSelected: boolean;
}

type SelectableRowSpec = {
  optionId: string | null;
  label: string | null;
  price: number | null;
};

const ProductCard = ({
  product,
  formatPrice,
  onToggleItem,
  onIncreaseQuantity,
  onDecreaseQuantity,
  onToggleDeliveryFee,
  onSelectDeliveryFeeOption,
  isItemSelected,
  getItemQuantity,
  deliveryState,
  isProductSelected,
}: ProductCardProps) => {
  const productId = product._id;
  const hasOptions = product.options.length > 0;
  const productPrice = product.price?.amount ?? null;

  const rows: SelectableRowSpec[] = hasOptions
    ? product.options.map((option) => ({
        optionId: option._id,
        label: option.name,
        price: option.price?.amount ?? null,
      }))
    : [
        {
          optionId: null,
          label: null,
          price: productPrice && productPrice > 0 ? productPrice : null,
        },
      ];

  return (
    <div className="product-card">
      <div className="product-header">
        {product.thumbnail && (
          <img
            className="product-thumbnail"
            src={product.thumbnail.thumbnail}
            alt={product.name}
          />
        )}
        <div className="product-name">{product.name}</div>
      </div>

      <div className="selectable-rows">
        {rows.map((row) => {
          const selected = isItemSelected(productId, row.optionId);
          const quantity = getItemQuantity(productId, row.optionId);
          const isPriceMissing = row.price == null;

          const handleRowToggle = () => {
            if (row.price == null) return;
            onToggleItem({ productId, optionId: row.optionId, price: row.price });
          };

          return (
            <div
              key={row.optionId ?? 'base'}
              className={`selectable-row ${selected ? 'selected' : ''} ${isPriceMissing ? 'disabled' : ''}`}
              role={isPriceMissing ? undefined : 'checkbox'}
              aria-checked={selected}
              tabIndex={isPriceMissing ? -1 : 0}
              onClick={handleRowToggle}
              onKeyDown={(e) => {
                if (isPriceMissing) return;
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault();
                  handleRowToggle();
                }
              }}
            >
              <div className="row-left">
                <input
                  type="checkbox"
                  className="row-checkbox"
                  disabled={isPriceMissing}
                  checked={selected}
                  readOnly
                  tabIndex={-1}
                />
                {isPriceMissing ? (
                  <div className="row-label">
                    {row.label && <span className="option-name">{row.label}</span>}
                    <div className="no-price-wrapper">
                      <span className="no-price-text">가격 정보 없음</span>
                      <span className="need-to-write">직접 입력이 필요해요</span>
                    </div>
                  </div>
                ) : (
                  <div className="row-label">
                    {row.label && <span className="option-name">{row.label}</span>}
                    <span className="row-price">{formatPrice(row.price!)}</span>
                  </div>
                )}
              </div>
              {!isPriceMissing && (
                <div
                  className="quantity-stepper-wrapper"
                  onClick={(e) => e.stopPropagation()}
                >
                  <QuantityStepper
                    productId={productId}
                    optionId={row.optionId}
                    isSelected={selected}
                    quantity={quantity}
                    onIncrease={onIncreaseQuantity}
                    onDecrease={onDecreaseQuantity}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {product.deliveryFeeOptions.length > 0 && (
        <DeliveryFeeSection
          product={product}
          isProductSelected={isProductSelected}
          deliveryState={deliveryState}
          formatPrice={formatPrice}
          onToggle={onToggleDeliveryFee}
          onSelectOption={onSelectDeliveryFeeOption}
        />
      )}
    </div>
  );
};

export default ProductCard;
